import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const WorkbenchContext = createContext();

export const useWorkbench = () => {
  const context = useContext(WorkbenchContext);
  if (!context) {
    throw new Error('useWorkbench must be used within WorkbenchProvider');
  }
  return context;
};

// Generate UUID for case IDs
const generateId = () => {
  return 'case_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
};

export const WorkbenchProvider = ({ children }) => {
  // Case Management State
  const [cases, setCases] = useState(() => {
    const saved = localStorage.getItem('legal_myCases');
    if (saved) {
      // Ensure all cases have artifacts array (migration for existing data)
      const parsed = JSON.parse(saved);
      return parsed.map(c => ({
        ...c,
        artifacts: c.artifacts || []
      }));
    }
    return [];
  });
  const [activeCaseId, setActiveCaseId] = useState(() => {
    return localStorage.getItem('legal_activeCaseId') || null;
  });

  // Derived active case
  const activeCase = useMemo(() => {
    return cases.find(c => c.id === activeCaseId) || null;
  }, [cases, activeCaseId]);

  // Persist cases to localStorage
  useEffect(() => {
    localStorage.setItem('legal_myCases', JSON.stringify(cases));
  }, [cases]);

  // Persist active case ID
  useEffect(() => {
    if (activeCaseId) {
      localStorage.setItem('legal_activeCaseId', activeCaseId);
    } else {
      localStorage.removeItem('legal_activeCaseId');
    }
  }, [activeCaseId]);

  // Case CRUD operations
  const createCase = (caseData) => {
    const newCase = {
      id: generateId(),
      name: caseData.name,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      linkedReferences: [],
      documents: [],
      artifacts: []
    };
    setCases(prev => [newCase, ...prev]);
    return newCase;
  };

  const updateCase = (caseId, updates) => {
    setCases(prev => prev.map(c =>
      c.id === caseId
        ? { ...c, ...updates, updatedAt: new Date().toISOString() }
        : c
    ));
  };

  const deleteCase = (caseId) => {
    setCases(prev => prev.filter(c => c.id !== caseId));
    if (activeCaseId === caseId) {
      setActiveCaseId(null);
    }
  };

  const linkReferenceToCase = (caseId, referenceId) => {
    setCases(prev => prev.map(c => {
      if (c.id === caseId && !c.linkedReferences.includes(referenceId)) {
        return {
          ...c,
          linkedReferences: [...c.linkedReferences, referenceId],
          updatedAt: new Date().toISOString()
        };
      }
      return c;
    }));
  };

  const unlinkReferenceFromCase = (caseId, referenceId) => {
    setCases(prev => prev.map(c => {
      if (c.id === caseId) {
        return {
          ...c,
          linkedReferences: c.linkedReferences.filter(id => id !== referenceId),
          updatedAt: new Date().toISOString()
        };
      }
      return c;
    }));
  };

  const addDocumentToCase = (caseId, document) => {
    setCases(prev => prev.map(c => {
      if (c.id === caseId) {
        return {
          ...c,
          documents: [...c.documents, document],
          updatedAt: new Date().toISOString()
        };
      }
      return c;
    }));
  };

  // Add or update artifact in a case (stores full artifact object)
  const addArtifactToCase = useCallback((caseId, artifact) => {
    setCases(prev => prev.map(c => {
      if (c.id === caseId) {
        // Check if artifact with same title exists (update) or add new
        const existingIndex = c.artifacts.findIndex(a => a.title === artifact.title);
        let newArtifacts;
        if (existingIndex >= 0) {
          // Update existing artifact
          newArtifacts = [...c.artifacts];
          newArtifacts[existingIndex] = {
            ...newArtifacts[existingIndex],
            ...artifact,
            updatedAt: new Date().toISOString()
          };
        } else {
          // Add new artifact
          newArtifacts = [{
            ...artifact,
            id: artifact.id || Date.now().toString(),
            createdAt: artifact.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }, ...c.artifacts];
        }
        return {
          ...c,
          artifacts: newArtifacts,
          updatedAt: new Date().toISOString()
        };
      }
      return c;
    }));
  }, []);

  // Remove artifact from a case
  const removeArtifactFromCase = useCallback((caseId, artifactId) => {
    setCases(prev => prev.map(c => {
      if (c.id === caseId) {
        return {
          ...c,
          artifacts: c.artifacts.filter(a => a.id !== artifactId),
          updatedAt: new Date().toISOString()
        };
      }
      return c;
    }));
  }, []);

  // Existing state
  const [containerLength, setContainerLength] = useState(5.4);
  const [containerWidth, setContainerWidth] = useState(2.6);
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [optimizedLayout, setOptimizedLayout] = useState(null);
  const [activeTab, setActiveTab] = useState('my-cases');
  const [currentDocument, setCurrentDocument] = useState(null);
  const [extractionResult, setExtractionResult] = useState(null);
  const [uploadedRequirementsFiles, setUploadedRequirementsFiles] = useState([]); // For Requirements Gathering

  // DERIVED: requirementsOutputs comes from the active case's artifacts
  // This provides backwards compatibility while artifacts are stored per-case
  const requirementsOutputs = useMemo(() => {
    return activeCase?.artifacts || [];
  }, [activeCase?.artifacts]);

  // setRequirementsOutputs - updates the active case's artifacts
  // This wrapper maintains API compatibility with existing code
  const setRequirementsOutputs = useCallback((updater) => {
    if (!activeCaseId) return;

    setCases(prev => prev.map(c => {
      if (c.id === activeCaseId) {
        const currentArtifacts = c.artifacts || [];
        const newArtifacts = typeof updater === 'function' ? updater(currentArtifacts) : updater;
        return {
          ...c,
          artifacts: newArtifacts,
          updatedAt: new Date().toISOString()
        };
      }
      return c;
    }));
  }, [activeCaseId]); // Generated markdown outputs - now stored per-case
  const [selectedRequirementOutput, setSelectedRequirementOutput] = useState(null); // Selected output to view
  const [currentRequirementsDocument, setCurrentRequirementsDocument] = useState(null); // Current document context
  const [selectedRequirementFileIds, setSelectedRequirementFileIds] = useState(new Set()); // Selected files for analysis
  const [artifactTitle, setArtifactTitle] = useState(''); // Artifact title synced with AI Assistant

  // Package Design state
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState(new Set());
  const [selectedRequirementsArtifacts, setSelectedRequirementsArtifacts] = useState(new Set());
  const [currentPackageDesign, setCurrentPackageDesign] = useState(null);
  const [packageDesignOutputs, setPackageDesignOutputs] = useState(() => {
    const saved = localStorage.getItem('legal_packageDesignOutputs');
    return saved ? JSON.parse(saved) : [];
  });
  const [packageDesignTitle, setPackageDesignTitle] = useState('Package Design');
  const [isGeneratingPackageDesign, setIsGeneratingPackageDesign] = useState(false);

  // Reference Library navigation state
  // Used to highlight a specific case when navigating from a citation click
  const [selectedReferenceCase, setSelectedReferenceCase] = useState(null);

  // NOTE: requirementsOutputs is now derived from activeCase.artifacts
  // No separate localStorage persistence needed - it's saved with cases

  // Persist Package Design outputs to localStorage
  useEffect(() => {
    localStorage.setItem('legal_packageDesignOutputs', JSON.stringify(packageDesignOutputs));
  }, [packageDesignOutputs]);

  const value = {
    // Case Management
    cases,
    setCases,
    activeCaseId,
    setActiveCaseId,
    activeCase,
    createCase,
    updateCase,
    deleteCase,
    linkReferenceToCase,
    unlinkReferenceFromCase,
    addDocumentToCase,
    addArtifactToCase,
    removeArtifactFromCase,
    // Existing state
    containerLength,
    setContainerLength,
    containerWidth,
    setContainerWidth,
    selectedComponents,
    setSelectedComponents,
    optimizedLayout,
    setOptimizedLayout,
    activeTab,
    setActiveTab,
    currentDocument,
    setCurrentDocument,
    extractionResult,
    setExtractionResult,
    uploadedRequirementsFiles,
    setUploadedRequirementsFiles,
    requirementsOutputs,
    setRequirementsOutputs,
    selectedRequirementOutput,
    setSelectedRequirementOutput,
    currentRequirementsDocument,
    setCurrentRequirementsDocument,
    selectedRequirementFileIds,
    setSelectedRequirementFileIds,
    artifactTitle,
    setArtifactTitle,
    // Package Design
    selectedDocumentTypes,
    setSelectedDocumentTypes,
    selectedRequirementsArtifacts,
    setSelectedRequirementsArtifacts,
    currentPackageDesign,
    setCurrentPackageDesign,
    packageDesignOutputs,
    setPackageDesignOutputs,
    packageDesignTitle,
    setPackageDesignTitle,
    isGeneratingPackageDesign,
    setIsGeneratingPackageDesign,
    // Reference Library navigation
    selectedReferenceCase,
    setSelectedReferenceCase
  };

  return (
    <WorkbenchContext.Provider value={value}>
      {children}
    </WorkbenchContext.Provider>
  );
};
